import { FormEvent, ReactNode, useState } from 'react';
import { Button } from '@/components/ui';
import { FieldRenderer } from './fields/FieldRenderer';
import {
  FormDocumentFetcherProvider,
  type FormDocumentFetcher,
} from './FormDocumentFetcherContext';
import {
  FormPendingUploadsProvider,
  useFormPendingUploads,
} from './FormPendingUploadsContext';
import { FormUploadProvider, type FormUploadFn } from './FormUploadContext';
import { getVisibleGroups } from './helpers';
import { useDynamicForm } from './hooks/useDynamicForm';
import { useExistingSubmissionPrefill } from './hooks/useExistingSubmissionPrefill';
import type {
  ExistingSubmissionFetcher,
  FormSchema,
  FormStateValues,
  FormSubmitValues,
} from './types';

interface DynamicFormProps {
  schema: FormSchema;
  initialValues?: FormStateValues;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (payload: FormSubmitValues) => void;
  onCancel?: () => void;
  /** Banner shown above the submit button — typically a server-side error. */
  serverError?: string | null;
  /**
   * Callback used by file-picker widgets (`versioned_image_picker`,
   * `versioned_pdf_picker`) to upload one file. When omitted, those widgets
   * render in a disabled state with an "Uploads are not configured" notice.
   */
  uploadFile?: FormUploadFn;
  /**
   * Optional fetcher used by versioned-document picker widgets to resolve a
   * presigned URL for previewing an already-uploaded version. When omitted,
   * pickers render filename-only with no thumbnail / Open link.
   */
  documentFetcher?: FormDocumentFetcher;
  /**
   * When provided, the form watches `schema.identifier` fields and calls this
   * fetcher to prefill non-identifier fields from an existing submission
   * (e.g. for already-existing candidates). The fetcher is responsible for
   * its own endpoint binding (JWT vs invite mount).
   */
  existingSubmissionFetcher?: ExistingSubmissionFetcher;
  /**
   * Identifier values that come from the calling context rather than the
   * form itself — e.g. workflow forms with `identifier: ["entity_id"]` where
   * the modal knows the entity id but it's not a form field.
   */
  externalIdentifiers?: Record<string, string | number | undefined | null>;
}

/**
 * Group titles that are purely structural and shouldn't render a section
 * heading. The backend uses "Default" as a no-op title for ungrouped fields.
 */
const isGenericGroupTitle = (title: string | undefined): boolean => {
  if (!title) return true;
  return title.trim().toLowerCase() === 'default';
};

/**
 * Top-level metadata-driven form. Walks `schema.columns` (visible groups only),
 * renders each field via `FieldRenderer`, and runs full-form validation on submit.
 */
export const DynamicForm = (props: DynamicFormProps) => {
  let body: ReactNode = <DynamicFormInner {...props} />;
  // `FormPendingUploadsProvider` must wrap `DynamicFormInner` so the inner
  // component can read the registry. Picker fields rendered inside also
  // register with it.
  body = <FormPendingUploadsProvider>{body}</FormPendingUploadsProvider>;
  if (props.documentFetcher) {
    body = <FormDocumentFetcherProvider fetcher={props.documentFetcher}>{body}</FormDocumentFetcherProvider>;
  }
  if (props.uploadFile) {
    body = <FormUploadProvider uploadFile={props.uploadFile}>{body}</FormUploadProvider>;
  }
  return body;
};

const DynamicFormInner = ({
  schema,
  initialValues,
  submitting = false,
  submitLabel = 'Submit',
  onSubmit,
  onCancel,
  serverError = null,
  existingSubmissionFetcher,
  externalIdentifiers,
}: DynamicFormProps) => {
  const visibleGroups = getVisibleGroups(schema.columns);
  const { values, errors, setValue, setValues, validateAndPrepareSubmit } = useDynamicForm({
    groups: visibleGroups,
    initialValues,
  });

  useExistingSubmissionPrefill({
    schema,
    values,
    setValues,
    fetcher: existingSubmissionFetcher,
    externalIdentifiers,
  });

  // Tracks the picker-upload pass so the submit button reflects the right
  // state ("Uploading…" → "Submitting…") and disables during both phases.
  const [uploadingPending, setUploadingPending] = useState(false);
  const pendingRegistry = useFormPendingUploads();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || uploadingPending) return;

    let overlay: FormStateValues | undefined;
    if (pendingRegistry) {
      setUploadingPending(true);
      try {
        const result = await pendingRegistry.uploadAllPending();
        if (!result.success) {
          // Pickers surface their own per-entry errors. Don't proceed.
          return;
        }
        if (Object.keys(result.fieldValues).length > 0) {
          overlay = result.fieldValues;
        }
      } finally {
        setUploadingPending(false);
      }
    }

    const payload = validateAndPrepareSubmit(overlay);
    if (!payload) return;
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {visibleGroups.map((group, idx) => {
        const showHeading = !isGenericGroupTitle(group.title);
        return (
          <section key={`${group.title}-${idx}`} className="card">
            {showHeading && (
              <div className="card-header">
                <h2 className="text-base font-semibold text-rgf-gray-900">{group.title}</h2>
              </div>
            )}
            <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.items.map((field) => (
                <FieldRenderer
                  key={field.title}
                  field={field}
                  value={values[field.title]}
                  error={errors[field.title]}
                  onChange={(next) => setValue(field.title, next)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {serverError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {serverError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={submitting || uploadingPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting || uploadingPending}>
          {uploadingPending ? 'Uploading…' : submitting ? 'Submitting…' : submitLabel}
        </Button>
      </div>
    </form>
  );
};
